/**
 * Enhanced Document Upload Analytics Hook
 * Provides comprehensive tracking for document-related user actions
 */

import { useCallback } from 'react';
import { useAnalyticsContext } from '../contexts/AnalyticsContext';
import { useDocumentAnalytics } from './useAnalytics';
import { ANALYTICS_EVENTS } from '../lib/analyticsTypes';

export const useEnhancedDocumentAnalytics = () => {
  // Be resilient in tests where AnalyticsProvider may be absent
  let trackEvent: (event: string, props?: Record<string, any>) => void = () => {};
  try {
    // This will throw if provider is missing; we'll catch and use a no-op
    ({ trackEvent } = useAnalyticsContext());
  } catch {
    // no-op fallback for tests
    trackEvent = () => {};
  }
  const { trackDocumentUpload, trackDocumentAnalyzed } = useDocumentAnalytics();

  // Track upload zone interaction
  const trackUploadZoneAccessed = useCallback((accessMethod: 'click' | 'drag' | 'keyboard') => {
    trackEvent(ANALYTICS_EVENTS.UPLOAD_ZONE_ACCESSED, {
      access_method: accessMethod,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Track upload start with detailed file information
  const trackDocumentUploadStart = useCallback((
    files: FileList | File[],
    uploadMethod: 'drag_drop' | 'click_upload' | 'paste'
  ) => {
    const fileArray = Array.from(files);
    
    fileArray.forEach((file, index) => {
      const fileSizeMB = file.size / (1024 * 1024);
      const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
      
      trackDocumentUpload(file.type || fileExtension, fileSizeMB, uploadMethod);
      
      // Additional detailed tracking
      trackEvent('document_upload_started', {
        file_name_length: file.name.length,
        file_size_mb: fileSizeMB,
        file_extension: fileExtension,
        file_type: file.type,
        upload_method: uploadMethod,
        upload_batch_size: fileArray.length,
        upload_batch_index: index,
        timestamp: new Date().toISOString()
      });
    });

    // Track batch upload if multiple files
    if (fileArray.length > 1) {
      trackEvent('document_batch_upload', {
        total_files: fileArray.length,
        total_size_mb: fileArray.reduce((sum, file) => sum + file.size / (1024 * 1024), 0),
        upload_method: uploadMethod,
        file_types: [...new Set(fileArray.map(f => f.type || 'unknown'))],
        timestamp: new Date().toISOString()
      });
    }
  }, [trackDocumentUpload, trackEvent]);

  // Track upload progress and completion
  const trackDocumentUploadProgress = useCallback((
    fileName: string,
    progressPercent: number,
    uploadSpeed?: number
  ) => {
    // Only track certain progress milestones to avoid too many events
    const milestones = [25, 50, 75];
    if (milestones.includes(Math.floor(progressPercent))) {
      trackEvent('document_upload_progress', {
        file_name_hash: fileName.substring(0, 10), // First 10 chars for privacy
        progress_percent: progressPercent,
        upload_speed_mbps: uploadSpeed,
        timestamp: new Date().toISOString()
      });
    }
  }, [trackEvent]);

  // Track upload completion with processing details
  const trackDocumentUploadComplete = useCallback((
    fileName: string,
    fileSize: number,
    uploadTimeMs: number,
    processingTimeMs?: number,
    documentId?: string
  ) => {
    const fileSizeMB = fileSize / (1024 * 1024);
    
    trackEvent('document_upload_completed', {
      file_size_mb: fileSizeMB,
      upload_duration_ms: uploadTimeMs,
      upload_speed_mbps: fileSizeMB / (uploadTimeMs / 1000) / 8, // Rough calculation
      processing_time_ms: processingTimeMs,
      document_id: documentId,
      timestamp: new Date().toISOString()
    });

    // Also track the analyzed event if processing completed
    if (processingTimeMs && documentId) {
      trackDocumentAnalyzed(documentId, processingTimeMs);
    }
  }, [trackEvent, trackDocumentAnalyzed]);

  // Track upload failure with error details
  const trackDocumentUploadFailed = useCallback((
    fileName: string,
    errorType: string,
    errorMessage: string,
    uploadMethod: 'drag_drop' | 'click_upload' | 'paste',
    failureStage: 'validation' | 'upload' | 'processing'
  ) => {
    trackEvent(ANALYTICS_EVENTS.UPLOAD_FAILED, {
      file_name_length: fileName.length,
      error_type: errorType,
      error_message: errorMessage.substring(0, 200), // Truncate for privacy
      upload_method: uploadMethod,
      failure_stage: failureStage,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Track document interaction after upload
  const trackDocumentViewed = useCallback((documentId: string, documentType: string) => {
    trackEvent('document_viewed', {
      document_id: documentId,
      document_type: documentType,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackDocumentDeleted = useCallback((documentId: string, documentType: string, deleteReason?: string) => {
    trackEvent(ANALYTICS_EVENTS.DOCUMENT_DELETED, {
      document_id: documentId,
      document_type: documentType,
      delete_reason: deleteReason,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackDocumentReprocessed = useCallback((documentId: string, reprocessReason: string) => {
    trackEvent(ANALYTICS_EVENTS.DOCUMENT_REPROCESSED, {
      document_id: documentId,
      reprocess_reason: reprocessReason,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Track document search and filtering
  const trackDocumentSearch = useCallback((searchTerm: string, resultsCount: number) => {
    trackEvent('document_searched', {
      search_term_length: searchTerm.length,
      search_term_words: searchTerm.split(' ').length,
      results_count: resultsCount,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackDocumentFilterApplied = useCallback((filterType: string, filterValue: string) => {
    trackEvent('document_filter_applied', {
      filter_type: filterType,
      filter_value: filterValue,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  // Track document export/download
  const trackDocumentExported = useCallback((documentId: string, exportFormat: string) => {
    trackEvent('document_exported', {
      document_id: documentId,
      export_format: exportFormat,
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  return {
    // Upload flow tracking
    trackUploadZoneAccessed,
    trackDocumentUploadStart,
    trackDocumentUploadProgress,
    trackDocumentUploadComplete,
    trackDocumentUploadFailed,
    
    // Document interaction tracking
    trackDocumentViewed,
    trackDocumentDeleted,
    trackDocumentReprocessed,
    
    // Document discovery tracking
    trackDocumentSearch,
    trackDocumentFilterApplied,
    trackDocumentExported
  };
};
